import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, getToken, setToken } from './api';
import { ROLE, type Permission, type RoleId } from './permissions';

export interface User {
  userId: number;
  username: string;
  name: string;
  rollId: number | null;
  /** Resolved server-side. Present on the token and refreshed from /api/me. */
  permissions: Permission[];
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function decodeJwt(token: string): User | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof json !== 'object' || !json) return null;
    return {
      userId: json.userId,
      username: json.username,
      name: json.name,
      rollId: json.rollId ?? null,
      permissions: Array.isArray(json.permissions) ? json.permissions : [],
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const t = getToken();
    return t ? decodeJwt(t) : null;
  });
  const [loading, setLoading] = useState(false);

  /**
   * Re-read the session from the server once on mount.
   *
   * A token lives for hours; a role change takes effect immediately on the API.
   * Without this, someone whose role was just changed keeps seeing the old
   * buttons until the token expires, and every click 403s with no explanation.
   */
  const refresh = useCallback(async () => {
    if (!getToken()) return;
    try {
      const me = await api<User>('/api/me');
      setUser(me);
    } catch {
      // Offline or an expired token — the decoded token stands until a request
      // actually comes back 401, which the api helper already handles.
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      async login(username, password) {
        setLoading(true);
        try {
          const r = await api<{ token: string; user: User }>('/api/login', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
          });
          setToken(r.token);
          setUser(r.user);
        } finally {
          setLoading(false);
        }
      },
      async loginWithGoogle(credential) {
        setLoading(true);
        try {
          const r = await api<{ token: string; user: User }>('/api/login/google', {
            method: 'POST',
            body: JSON.stringify({ credential }),
          });
          setToken(r.token);
          setUser(r.user);
        } finally {
          setLoading(false);
        }
      },
      logout() {
        setToken(null);
        setUser(null);
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}

/**
 * Does this user hold every listed capability?
 *
 * Ask this, not "is this user an admin". Admin is read-only now; any code that
 * still branches on the role name would silently re-grant writes to it.
 *
 * This controls RENDERING only. requirePermission on the API is the enforcement.
 */
export function can(user: User | null, ...needed: Permission[]): boolean {
  if (!user) return false;
  if (needed.length === 0) return true;
  const held = new Set(user.permissions ?? []);
  return needed.every((p) => held.has(p));
}

/** Convenience hook — `const allowed = useCan('EDIT_PO')`. */
export function useCan(...needed: Permission[]): boolean {
  const { user } = useAuth();
  return can(user, ...needed);
}

/** Only accounts holding DELETE_RECORDS see Delete buttons. */
export function canDelete(user: User | null): boolean {
  return can(user, 'DELETE_RECORDS');
}

export { ROLE };
export type { Permission, RoleId };
